import {getDefaultRouteForRole, ROUTE_PATHS} from '@/router/routePaths.ts';
import {LoginPage} from '@/shared/components/LoginPage.tsx';
import {LoadingScreen} from '@/shared/components/ui/loading/LoadingScreen.tsx';
import {useAuth} from '@/shared/contexts/AuthContext.tsx';
import {Navigate, useLoaderData} from 'react-router';

type RootLoaderData = {
    isAuthenticated: boolean;
    isOAuthCallback?: boolean;
};

/**
 * Component that handles the root path redirect.
 * Redirects authenticated users to their role-specific dashboard.
 *
 * Auth flow:
 * 1. Loader (rootLoader) does fast sync check of localStorage session
 * 2. Component waits for AuthContext to load role from Supabase
 * 3. Redirects to appropriate dashboard based on role
 */
export const RootRedirect = () => {
    const loaderData = useLoaderData() as RootLoaderData;
    const {loading, isAuthenticated, hasAccess, role} = useAuth();

    // If OAuth callback in progress, wait for AuthContext to process the session
    if (loaderData.isOAuthCallback && loading) {
        return <LoadingScreen message="Autenticando..." />;
    }

    // Fast path: loader already determined not authenticated (and no OAuth callback)
    if (!loaderData.isAuthenticated && !loaderData.isOAuthCallback) {
        return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
    }

    // Wait for AuthContext to load role data from Supabase
    if (loading) {
        return <LoadingScreen message="Carregando..." />;
    }

    // After OAuth callback processed, check if we're actually authenticated
    if (!isAuthenticated) {
        return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
    }

    // User authenticated but not in employees table
    if (!hasAccess) {
        return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
    }

    // Redirect to role-specific dashboard
    if (role) {
        return <Navigate to={getDefaultRouteForRole(role)} replace />;
    }

    // Fallback to login if no role (shouldn't happen if hasAccess is true)
    return <Navigate to={ROUTE_PATHS.LOGIN} replace />;
};

/**
 * Login page wrapper.
 *
 * The redirectIfAuthenticated loader handles the fast redirect if already logged in.
 * This component adds a secondary check via AuthContext for edge cases where
 * the session might have been established after the loader ran.
 */
export const LoginPageWrapper = () => {
    const {loading, isAuthenticated, hasAccess, role} = useAuth();

    // Show loading while AuthContext initializes
    if (loading) {
        return <LoadingScreen message="Carregando..." />;
    }

    // Safety check: redirect if authenticated (loader should catch this, but just in case)
    if (isAuthenticated && hasAccess && role) {
        return <Navigate to={getDefaultRouteForRole(role)} replace />;
    }

    return <LoginPage />;
};
